import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import classNames from "classnames";
import { Picker } from "@design-system/Picker/Picker.lib";
import { memo, useCallback, useContext, useMemo, useState } from "react";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import {
    CounterpartyCellVariant,
    CounterpartyCellVariantSchema,
    ReviewColumnMetaSchema,
} from "@common/types/views/transactionViews";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import { getMatchedDocumentFieldLock } from "@common/helpers/matched-document-transaction-fields";
import { addTransactionFieldReviews } from "@api/transaction.api";
import {
    useIsReadonlyOrLocked,
    useIsTableReadonly,
} from "@hooks/useIsReadonlyOrLocked";
import { useShowCounterparty } from "@hooks/useShowCounterparty";
import { useUser } from "@hooks/useUser";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    CounterpartyCombobox,
    CounterpartyComboboxValue,
} from "../TransactionSplitV2/CounterpartyCombobox";
import { PaginatedTransactionsActionsContext } from "../PaginatedTransactionsTable.context";
import { updateTransactionInCache } from "../useTransactionsQuery";
import { TransactionFieldReviewButton } from "./TransactionFieldReviewButton";
import {
    isTransactionFieldReviewedByUser,
    mergeTransactionFieldReviews,
} from "./transactionFieldReviewUtils";
import styles from "./CounterpartyCell.module.scss";

export const CounterpartyCell = memo(
    ({
        row,
        table,
        column,
    }: CellContext<ViewTableFeatures, TransactionListItem, unknown>) => {
        const user = useUser();
        const { error: errorToast } = useToaster();
        const { saveTransaction } = useContext(
            PaginatedTransactionsActionsContext,
        );
        const isReadonlyOrLocked = useIsReadonlyOrLocked(row.original, table);
        const isTableReadonly = useIsTableReadonly(table);
        const { isLocked: isMatchLocked, tooltip: matchLockTooltip } =
            getMatchedDocumentFieldLock({
                matchLists: [
                    row.original.transactionMatches,
                    row.original.accrualMatches,
                ],
                isOtherwiseLocked: isReadonlyOrLocked,
            });
        const isDisabled = isReadonlyOrLocked || isMatchLocked;
        const [isOpen, setIsOpen] = useState(false);

        const metaParse = ReviewColumnMetaSchema.safeParse(
            column.columnDef.meta,
        );
        const rawVariant =
            metaParse.data?.customVariants?.variant ??
            metaParse.data?.variant ??
            "full";
        const variantParse =
            CounterpartyCellVariantSchema.safeParse(rawVariant);
        const variant: CounterpartyCellVariant = variantParse.success
            ? variantParse.data
            : "full";
        const isReviewVariant = variant === "review";

        const isReviewed = useMemo(
            () =>
                isTransactionFieldReviewedByUser(
                    row.original.fieldReviews,
                    TransactionReviewField.COUNTERPARTY,
                    user?.id,
                ),
            [row.original.fieldReviews, user?.id],
        );

        const handleUpdate = useCallback(
            async (counterparty?: CounterpartyComboboxValue) => {
                if (isDisabled) {
                    return;
                }
                await saveTransaction(row.original, {
                    counterpartyId: counterparty?.id ?? null,
                });
                // In review variant, automatically mark the counterparty field
                // as reviewed after a successful change. The change has already
                // committed, so surface a toast rather than letting a failed
                // review mark fail silently.
                if (isReviewVariant && counterparty?.id) {
                    try {
                        const reviews = await addTransactionFieldReviews(
                            row.original.id,
                            [TransactionReviewField.COUNTERPARTY],
                        );
                        updateTransactionInCache(row.original.id, (t) => ({
                            ...t,
                            fieldReviews: mergeTransactionFieldReviews(
                                t.fieldReviews,
                                reviews,
                            ),
                        }));
                    } catch {
                        errorToast(
                            "Failed to mark counterparty as reviewed. Please try again.",
                        );
                    }
                }
            },
            [
                saveTransaction,
                row.original,
                isDisabled,
                isReviewVariant,
                errorToast,
            ],
        );

        const handleCreationModalOpenChange = useCallback(
            (creationModalOpen: boolean) => {
                if (creationModalOpen) {
                    setIsOpen(false);
                }
            },
            [],
        );

        const showCounterparty = useShowCounterparty();
        const counterpartyToShow = showCounterparty(row.original.counterparty)
            ? row.original.counterparty
            : undefined;

        const trigger = useMemo(
            () => (
                <Picker.CellContent
                    readOnly={isDisabled}
                    buttonProps={{ tooltip: matchLockTooltip }}
                    showDisabledAdd={!!matchLockTooltip}
                    className={classNames({
                        [styles.reviewVariant]: isReviewVariant,
                        [styles.isReviewed]: isReviewed,
                    })}
                    value={counterpartyToShow?.name}
                />
            ),
            [
                counterpartyToShow,
                isDisabled,
                isReviewVariant,
                isReviewed,
                matchLockTooltip,
            ],
        );

        return (
            <ViewTableCell captureClicks className={styles.counterpartyCell}>
                <CounterpartyCombobox
                    selectedCounterparty={counterpartyToShow ?? undefined}
                    onChange={handleUpdate}
                    forTransactionEditing
                    disabled={isDisabled}
                    hideArrow
                    open={isOpen}
                    onOpenChange={setIsOpen}
                    onCreationModalOpenChange={handleCreationModalOpenChange}
                    trigger={trigger}
                />

                {isReviewVariant && (
                    <TransactionFieldReviewButton
                        transaction={row.original}
                        isReadonly={isTableReadonly}
                        field={TransactionReviewField.COUNTERPARTY}
                        reviewTooltip="Review counterparty"
                        size="lg"
                    />
                )}
            </ViewTableCell>
        );
    },
);
CounterpartyCell.displayName = "CounterpartyCell";
